import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon, Download as DownloadIcon } from "@mui/icons-material";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";

const PolicyPreviewDialog = ({ open, onClose, policy }) => {
  if (!policy) return null;

  // Handle DOCX conversion to PDF
  const fileUrl = `${process.env.REACT_APP_CLOUDFRONT_URL}/${
    policy.filePath?.toLowerCase().endsWith(".docx")
      ? policy.filePath.replace(/\.docx$/i, ".pdf")
      : policy.filePath
  }`;

  const handleDownload = async () => {
    if (policy.filePath) {
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = policy.fileName || policy.policyFileName || "policy.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error downloading file:", error);
        window.open(fileUrl, "_blank");
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
          borderRadius: "12px",
          maxWidth: "1100px",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #E5E7EB",
          fontWeight: 600,
          fontSize: "18px",
          py: 2,
          px: 2.5,
          backgroundColor: "#F7F7FC",
        }}
      >
        {policy.policyDisplayName || policy.policyName || policy.fileName}
        <Tooltip title="Download Policy">
          <IconButton
            onClick={handleDownload}
            sx={{
              position: "absolute",
              right: 56,
              top: 12,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
        <Grid container sx={{ height: "100%" }}>
          {/* Left: Metadata */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              p: 2.5,
              borderRight: "1px solid #E5E7EB",
              overflowY: "auto",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "10px",
                }}
              >
                Policy Name
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 0.5,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {policy.policyDisplayName || policy.policyName || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "10px",
                }}
              >
                File Name
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 0.5,
                  color: "text.secondary",
                  wordBreak: "break-word",
                  fontSize: "13px",
                }}
              >
                {policy.fileName || policy.policyFileName || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "10px",
                }}
              >
                Version
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "13px",
                }}
              >
                v{policy.fileVersion || "1"}
              </Typography>
            </Box>

            {policy.category && (
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    fontSize: "10px",
                  }}
                >
                  Category
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5,
                    textTransform: "capitalize",
                    fontSize: "13px",
                  }}
                >
                  {policy.category}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Right: Document Viewer */}
          <Grid
            item
            xs={12}
            md={9}
            sx={{
              height: "100%",
              overflow: "hidden",
              bgcolor: "#F3F4F6",
              p: 1,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #E5E7EB",
                backgroundColor: "#FFFFFF",
              }}
            >
              <PdfViewer fileUrl={fileUrl} fillHeight />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyPreviewDialog;
